import { useState } from "react";
import { X, Move, Hand, MousePointerClick, SatelliteDish, ListChecks, Plus } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";

/**
 * Le guide d'accueil de l'atelier, montré une seule fois, par-dessus l'image.
 *
 * DEUX guides, pas un — parce qu'il y a deux ateliers derrière la même page.
 *
 * `trace` : l'IA a proposé un contour et le couvreur doit l'ajuster. Trois
 * gestes différents sur trois cibles qui se ressemblent (glisser la forme,
 * tirer un coin, ajouter un coin sur un cercle blanc) ; sans explication, un
 * couvreur croit que le contour de l'IA est définitif.
 *
 * `satellite` : Sat Premium a mesuré le bâtiment, il n'y a plus rien à tracer.
 * Lui expliquer comment tirer un coin serait décrire une interface qu'il n'a
 * pas sous les yeux. Ce qu'il doit savoir est ailleurs : vérifier que c'est
 * bien la maison de son client, décocher les pans qu'il ne refait pas, et
 * ajouter les dépendances.
 *
 * Chaque variante a sa propre mémoire : avoir vu l'un ne dispense pas de voir
 * l'autre le jour où on tombe dessus. Trois étapes, pas dix, et une seule
 * fois — un guide qui revient à chaque visite se fait fermer sans être lu.
 */
const T = {
  fr: {
    passer: "Passer",
    suivant: "Suivant",
    fini: "Compris",
    satellite: [
      { icone: SatelliteDish, titre: "Le bâtiment est déjà mesuré",
        corps: "Le rectangle bleu montre exactement ce que le satellite a mesuré. Vérifiez que c'est bien la maison de votre client : il retient le bâtiment le plus proche de l'adresse, ce qui n'est pas toujours le bon sur un grand terrain." },
      { icone: ListChecks, titre: "Décochez ce que vous ne refaites pas",
        corps: "Le toit est découpé en pans, chacun avec sa pente et sa surface. Décochez l'aile du garage ou l'appentis que le contrat ne couvre pas : le total suit." },
      { icone: Plus, titre: "Ajoutez les cabanons",
        corps: "« Ajouter un cabanon ou un bâtiment » pose un contour à glisser sur la dépendance ; le satellite la mesure seule. Si elle est hors de l'image, « Élargir la vue » recule la caméra." },
    ],
    trace: [
      { icone: Move, titre: "Le contour est déjà tracé",
        corps: "L'IA a détecté le toit. Glissez la forme entière pour la recentrer si elle est décalée." },
      { icone: Hand, titre: "Tirez un coin pour le corriger",
        corps: "Chaque point orange se déplace. C'est ce qui rattrape un contour qui déborde sur le garage ou coupe une rallonge." },
      { icone: MousePointerClick, titre: "Ajoutez ou enlevez des coins",
        corps: "Cliquez un cercle blanc entre deux points pour en ajouter un. Double-cliquez un coin pour l'enlever — sur téléphone, trois tapes." },
    ],
  },
  en: {
    passer: "Skip",
    suivant: "Next",
    fini: "Got it",
    satellite: [
      { icone: SatelliteDish, titre: "The building is already measured",
        corps: "The blue rectangle shows exactly what the satellite measured. Check it is your customer's house: it keeps the building closest to the address, which is not always the right one on a large lot." },
      { icone: ListChecks, titre: "Untick what you are not redoing",
        corps: "The roof comes split into faces, each with its own pitch and area. Untick the garage wing or the lean-to the contract does not cover — the total follows." },
      { icone: Plus, titre: "Add the sheds",
        corps: "“Add a shed or building” drops an outline to drag over the outbuilding; the satellite measures it on its own. If it sits outside the frame, “Wider view” pulls the camera back." },
    ],
    trace: [
      { icone: Move, titre: "The outline is already traced",
        corps: "The AI detected the roof. Drag the whole shape to re-center it if it sits off." },
      { icone: Hand, titre: "Drag a corner to fix it",
        corps: "Every orange point moves. This is what fixes an outline spilling onto the garage or cutting off an addition." },
      { icone: MousePointerClick, titre: "Add or remove corners",
        corps: "Click a white circle between two points to add one. Double-click a corner to remove it — on a phone, tap it three times." },
    ],
  },
  es: {
    passer: "Omitir",
    suivant: "Siguiente",
    fini: "Entendido",
    satellite: [
      { icone: SatelliteDish, titre: "El edificio ya está medido",
        corps: "El rectángulo azul muestra exactamente lo que midió el satélite. Verifique que sea la casa de su cliente: retiene el edificio más cercano a la dirección, que no siempre es el correcto en un terreno grande." },
      { icone: ListChecks, titre: "Desmarque lo que no va a rehacer",
        corps: "El techo viene dividido en faldones, cada uno con su pendiente y su superficie. Desmarque el ala del garaje o el cobertizo que el contrato no cubre: el total se ajusta." },
      { icone: Plus, titre: "Agregue los cobertizos",
        corps: "«Agregar un cobertizo o edificio» coloca un contorno para arrastrar sobre la construcción; el satélite la mide sola. Si queda fuera de la imagen, «Ampliar la vista» aleja la cámara." },
    ],
    trace: [
      { icone: Move, titre: "El contorno ya está trazado",
        corps: "La IA detectó el techo. Arrastre toda la forma para centrarla si quedó desplazada." },
      { icone: Hand, titre: "Arrastre una esquina para corregirla",
        corps: "Cada punto naranja se mueve. Así se corrige un contorno que invade el garaje o corta una ampliación." },
      { icone: MousePointerClick, titre: "Agregue o quite esquinas",
        corps: "Haga clic en un círculo blanco entre dos puntos para agregar uno. Doble clic en una esquina para quitarla — en el teléfono, tres toques." },
    ],
  },
};

// La variante « trace » garde l'ancienne clé : un couvreur qui a déjà vu le
// guide du tracé ne doit pas le revoir parce qu'on a ajouté une variante.
const CLES = { trace: "rb_guide_trace_vu", satellite: "rb_guide_satellite_vu" };
export const CLE_GUIDE_TRACE = CLES.trace;

/** Vrai tant que le couvreur n'a jamais vu CETTE variante du guide. */
export function guideTraceAVoir(variante = "trace") {
  try { return localStorage.getItem(CLES[variante] || CLES.trace) !== "1"; } catch { return false; }
}

export default function GuideTrace({ ouvert, onFermer, variante = "trace" }) {
  const { lang } = useI18n();
  const [i, setI] = useState(0);

  if (!ouvert) return null;

  const L = T[lang] || T.en;
  const etapes = L[variante] || L.trace;
  // Sat Premium peut répondre pendant la lecture et faire changer de variante :
  // l'étape courante doit rester dans les bornes du nouveau jeu.
  const etape = etapes[Math.min(i, etapes.length - 1)];
  const Icone = etape.icone;
  const dernier = i >= etapes.length - 1;

  const fermer = () => {
    try { localStorage.setItem(CLES[variante] || CLES.trace, "1"); } catch { /* navigation privée */ }
    setI(0);
    onFermer && onFermer();
  };

  // Sur téléphone la carte se pose SOUS l'image ; posée dessus, elle couvrirait
  // la moitié d'un toit carré et on ne verrait plus ce qu'elle décrit.
  return (
    <div
      data-testid="guide-trace"
      className="relative mt-3 sm:mt-0 sm:absolute sm:left-3 sm:bottom-3 sm:max-w-sm z-20
                 bg-surface/95 backdrop-blur border border-line-strong rounded-xl shadow-carte p-4"
    >
      <div className="flex items-start gap-3">
        <span className="h-8 w-8 shrink-0 rounded-full bg-orange-600/15 text-orange-500 flex items-center justify-center">
          <Icone className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-ink leading-snug">{etape.titre}</p>
          <p className="text-sm text-ink-soft mt-1">{etape.corps}</p>
        </div>
        <button
          onClick={fermer}
          data-testid="guide-trace-fermer"
          aria-label={L.passer}
          title={L.passer}
          className="text-ink-faint hover:text-ink transition-colors shrink-0 p-2 -m-2"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex items-center justify-between gap-3 mt-4">
        <div className="flex items-center gap-1.5" aria-hidden="true">
          {etapes.map((_, n) => (
            <span
              key={n}
              className={`h-1.5 rounded-full transition-all ${
                n === i ? "w-5 bg-orange-600" : "w-1.5 bg-line-strong"
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-3">
          {!dernier && (
            <button
              onClick={fermer}
              data-testid="guide-trace-passer"
              className="text-xs text-ink-faint hover:text-ink transition-colors"
            >
              {L.passer}
            </button>
          )}
          <Button
            onClick={() => (dernier ? fermer() : setI(i + 1))}
            data-testid="guide-trace-suivant"
            className="h-8 px-4 bg-orange-600 hover:bg-orange-700 rounded-md text-xs font-semibold"
          >
            {dernier ? L.fini : L.suivant}
          </Button>
        </div>
      </div>
    </div>
  );
}
